import { DOCUMENT, isPlatformBrowser, NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    contentChild,
    contentChildren,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    inject,
    input,
    OnChanges,
    OnDestroy,
    OnInit,
    output,
    PLATFORM_ID,
    signal,
    SimpleChanges,
    TemplateRef,
    untracked,
    viewChild,
    ViewRef
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Logger } from '@df/core/service/logger';
import { injectTimeoutService } from '@df/ng/timeout.service';
import Swiper from 'swiper';
import { SwiperContainer } from 'swiper/element';
import { register } from 'swiper/element/bundle';
import {
    A11y,
    Autoplay,
    Controller,
    EffectCards,
    EffectCoverflow,
    EffectCreative,
    EffectCube,
    EffectFade,
    EffectFlip,
    FreeMode,
    Grid,
    HashNavigation,
    History,
    Keyboard,
    Mousewheel,
    Navigation,
    Pagination,
    Parallax,
    Scrollbar,
    Thumbs,
    Zoom
} from 'swiper/modules';
import { SwiperModule } from 'swiper/types';
import { CarouselSlideDirective } from './carousel-slide.directive';
import { Carousel } from './carousel.interface';
import { Marquee } from './plugin/marquee';

@Component({
    selector: 'carousel',
    templateUrl: './carousel.component.html',
    styleUrl: './carousel.component.scss',
    standalone: true,
    imports: [NgTemplateOutlet],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarouselComponent implements OnInit, OnChanges, OnDestroy {
    // Parameters
    // @see https://swiperjs.com/swiper-api#parameters
    a11y = input<Carousel.Options['a11y']>();
    allowSlideNext = input<Carousel.Options['allowSlideNext']>();
    allowSlidePrev = input<Carousel.Options['allowSlidePrev']>();
    allowTouchMove = input<Carousel.Options['allowTouchMove']>();
    autoHeight = input<Carousel.Options['autoHeight']>();
    autoplay = input<Carousel.Options['autoplay']>();
    breakpoints = input<Carousel.Options['breakpoints']>();
    breakpointsBase = input<Carousel.Options['breakpointsBase']>();
    cardsEffect = input<Carousel.Options['cardsEffect']>();
    centerInsufficientSlides = input<Carousel.Options['centerInsufficientSlides']>();
    centeredSlides = input<Carousel.Options['centeredSlides']>();
    centeredSlidesBounds = input<Carousel.Options['centeredSlidesBounds']>();
    containerModifierClass = input<Carousel.Options['containerModifierClass']>();
    controller = input<Carousel.Options['controller']>();
    coverflowEffect = input<Carousel.Options['coverflowEffect']>();
    createElements = input<Carousel.Options['createElements']>();
    creativeEffect = input<Carousel.Options['creativeEffect']>();
    cssMode = input<Carousel.Options['cssMode']>();
    cubeEffect = input<Carousel.Options['cubeEffect']>();
    direction = input<Carousel.Options['direction']>();
    edgeSwipeDetection = input<Carousel.Options['edgeSwipeDetection']>();
    edgeSwipeThreshold = input<Carousel.Options['edgeSwipeThreshold']>();
    effect = input<Carousel.Options['effect']>();
    enabled = input<Carousel.Options['enabled']>();
    eventsPrefix = input<Carousel.Options['eventsPrefix']>();
    fadeEffect = input<Carousel.Options['fadeEffect']>();
    flipEffect = input<Carousel.Options['flipEffect']>();
    focusableElements = input<Carousel.Options['focusableElements']>();
    followFinger = input<Carousel.Options['followFinger']>();
    freeMode = input<Carousel.Options['freeMode']>();
    grabCursor = input<Carousel.Options['grabCursor']>();
    grid = input<Carousel.Options['grid']>();
    hashNavigation = input<Carousel.Options['hashNavigation']>();
    height = input<Carousel.Options['height']>();
    history = input<Carousel.Options['history']>();
    initialSlide = input<Carousel.Options['initialSlide']>();
    injectStyles = input<Carousel.Options['injectStyles']>();
    injectStylesUrls = input<Carousel.Options['injectStylesUrls']>();
    keyboard = input<Carousel.Options['keyboard']>();
    lazyPreloadPrevNext = input<Carousel.Options['lazyPreloadPrevNext']>();
    lazyPreloaderClass = input<Carousel.Options['lazyPreloaderClass']>();
    longSwipes = input<Carousel.Options['longSwipes']>();
    longSwipesMs = input<Carousel.Options['longSwipesMs']>();
    longSwipesRatio = input<Carousel.Options['longSwipesRatio']>();
    loop = input<Carousel.Options['loop']>();
    loopAddBlankSlides = input<Carousel.Options['loopAddBlankSlides']>();
    loopAdditionalSlides = input<Carousel.Options['loopAdditionalSlides']>();
    loopPreventsSliding = input<Carousel.Options['loopPreventsSliding']>();
    maxBackfaceHiddenSlides = input<Carousel.Options['maxBackfaceHiddenSlides']>();
    marquee = input<Carousel.Options['marquee']>();
    mousewheel = input<Carousel.Options['mousewheel']>();
    navigation = input<Carousel.Options['navigation']>();
    nested = input<Carousel.Options['nested']>();
    noSwiping = input<Carousel.Options['noSwiping']>();
    noSwipingClass = input<Carousel.Options['noSwipingClass']>();
    noSwipingSelector = input<Carousel.Options['noSwipingSelector']>();
    normalizeSlideIndex = input<Carousel.Options['normalizeSlideIndex']>();
    observeParents = input<Carousel.Options['observeParents']>();
    observeSlideChildren = input<Carousel.Options['observeSlideChildren']>();
    observer = input<Carousel.Options['observer']>();
    on = input<Carousel.Options['on']>();
    oneWayMovement = input<Carousel.Options['oneWayMovement']>();
    pagination = input<Carousel.Options['pagination']>();
    parallax = input<Carousel.Options['parallax']>();
    passiveListeners = input<Carousel.Options['passiveListeners']>();
    preventClicks = input<Carousel.Options['preventClicks']>();
    preventClicksPropagation = input<Carousel.Options['preventClicksPropagation']>();
    preventInteractionOnTransition = input<Carousel.Options['preventInteractionOnTransition']>();
    resistance = input<Carousel.Options['resistance']>();
    resistanceRatio = input<Carousel.Options['resistanceRatio']>();
    resizeObserver = input<Carousel.Options['resizeObserver']>();
    rewind = input<Carousel.Options['rewind']>();
    roundLengths = input<Carousel.Options['roundLengths']>();
    runCallbacksOnInit = input<Carousel.Options['runCallbacksOnInit']>();
    scrollbar = input<Carousel.Options['scrollbar']>();
    setWrapperSize = input<Carousel.Options['setWrapperSize']>();
    shortSwipes = input<Carousel.Options['shortSwipes']>();
    simulateTouch = input<Carousel.Options['simulateTouch']>();
    slideActiveClass = input<Carousel.Options['slideActiveClass']>();
    slideBlankClass = input<Carousel.Options['slideBlankClass']>();
    slideClass = input<Carousel.Options['slideClass']>();
    slideFullyVisibleClass = input<Carousel.Options['slideFullyVisibleClass']>();
    slideNextClass = input<Carousel.Options['slideNextClass']>();
    slidePrevClass = input<Carousel.Options['slidePrevClass']>();
    slideToClickedSlide = input<Carousel.Options['slideToClickedSlide']>();
    slideVisibleClass = input<Carousel.Options['slideVisibleClass']>();
    slidesOffsetAfter = input<Carousel.Options['slidesOffsetAfter']>();
    slidesOffsetBefore = input<Carousel.Options['slidesOffsetBefore']>();
    slidesPerGroup = input<Carousel.Options['slidesPerGroup']>();
    slidesPerGroupAuto = input<Carousel.Options['slidesPerGroupAuto']>();
    slidesPerGroupSkip = input<Carousel.Options['slidesPerGroupSkip']>();
    slidesPerView = input<Carousel.Options['slidesPerView']>();
    spaceBetween = input<Carousel.Options['spaceBetween']>();
    speed = input<Carousel.Options['speed']>();
    swipeHandler = input<Carousel.Options['swipeHandler']>();
    swiperElementNodeName = input<Carousel.Options['swiperElementNodeName']>();
    threshold = input<Carousel.Options['threshold']>();
    thumbs = input<Carousel.Options['thumbs']>();
    touchAngle = input<Carousel.Options['touchAngle']>();
    touchEventsTarget = input<Carousel.Options['touchEventsTarget']>();
    touchMoveStopPropagation = input<Carousel.Options['touchMoveStopPropagation']>();
    touchRatio = input<Carousel.Options['touchRatio']>();
    touchReleaseOnEdges = input<Carousel.Options['touchReleaseOnEdges']>();
    touchStartForcePreventDefault = input<Carousel.Options['touchStartForcePreventDefault']>();
    touchStartPreventDefault = input<Carousel.Options['touchStartPreventDefault']>();
    uniqueNavElements = input<Carousel.Options['uniqueNavElements']>();
    updateOnWindowResize = input<Carousel.Options['updateOnWindowResize']>();
    url = input<Carousel.Options['url']>();
    userAgent = input<Carousel.Options['userAgent']>();
    watchOverflow = input<Carousel.Options['watchOverflow']>();
    watchSlidesProgress = input<Carousel.Options['watchSlidesProgress']>();
    width = input<Carousel.Options['width']>();
    wrapperClass = input<Carousel.Options['wrapperClass']>();
    zoom = input<Carousel.Options['zoom']>();

    // Events
    // @see https://swiperjs.com/swiper-api#events
    s_activeIndexChange = output<Carousel.EventsParams['activeIndexChange']>({ alias: 'activeIndexChange' });
    s_afterInit = output<Carousel.EventsParams['afterInit']>({ alias: 'afterInit' });
    s_autoplay = output<Carousel.EventsParams['autoplay']>({ alias: 'autoplay' });
    s_autoplayPause = output<Carousel.EventsParams['autoplayPause']>({ alias: 'autoplayPause' });
    s_autoplayResume = output<Carousel.EventsParams['autoplayResume']>({ alias: 'autoplayResume' });
    s_autoplayStart = output<Carousel.EventsParams['autoplayStart']>({ alias: 'autoplayStart' });
    s_autoplayStop = output<Carousel.EventsParams['autoplayStop']>({ alias: 'autoplayStop' });
    s_autoplayTimeLeft = output<Carousel.EventsParams['autoplayTimeLeft']>({ alias: 'autoplayTimeLeft' });
    s_beforeDestroy = output<Carousel.EventsParams['beforeDestroy']>({ alias: 'beforeDestroy' });
    s_beforeInit = output<Carousel.EventsParams['beforeInit']>({ alias: 'beforeInit' });
    s_beforeLoopFix = output<Carousel.EventsParams['beforeLoopFix']>({ alias: 'beforeLoopFix' });
    s_beforeResize = output<Carousel.EventsParams['beforeResize']>({ alias: 'beforeResize' });
    s_beforeSlideChangeStart = output<Carousel.EventsParams['beforeSlideChangeStart']>({ alias: 'beforeSlideChangeStart' });
    s_beforeTransitionStart = output<Carousel.EventsParams['beforeTransitionStart']>({ alias: 'beforeTransitionStart' });
    s_breakpoint = output<Carousel.EventsParams['breakpoint']>({ alias: 'breakpoint' });
    s_changeDirection = output<Carousel.EventsParams['changeDirection']>({ alias: 'changeDirection' });
    s_click = output<Carousel.EventsParams['click']>({ alias: 'click' });
    s_destroy = output<Carousel.EventsParams['destroy']>({ alias: 'destroy' });
    s_doubleClick = output<Carousel.EventsParams['doubleClick']>({ alias: 'doubleClick' });
    s_doubleTap = output<Carousel.EventsParams['doubleTap']>({ alias: 'doubleTap' });
    s_fromEdge = output<Carousel.EventsParams['fromEdge']>({ alias: 'fromEdge' });
    s_init = output<Carousel.EventsParams['init']>({ alias: 'init' });
    s_lock = output<Carousel.EventsParams['lock']>({ alias: 'lock' });
    s_loopFix = output<Carousel.EventsParams['loopFix']>({ alias: 'loopFix' });
    s_marquee = output<Carousel.EventsParams['marquee']>({ alias: 'marquee' });
    s_marqueePause = output<Carousel.EventsParams['marqueePause']>({ alias: 'marqueePause' });
    s_marqueeResume = output<Carousel.EventsParams['marqueeResume']>({ alias: 'marqueeResume' });
    s_marqueeStart = output<Carousel.EventsParams['marqueeStart']>({ alias: 'marqueeStart' });
    s_marqueeStop = output<Carousel.EventsParams['marqueeStop']>({ alias: 'marqueeStop' });
    s_momentumBounce = output<Carousel.EventsParams['momentumBounce']>({ alias: 'momentumBounce' });
    s_navigationHide = output<Carousel.EventsParams['navigationHide']>({ alias: 'navigationHide' });
    s_navigationNext = output<Carousel.EventsParams['navigationNext']>({ alias: 'navigationNext' });
    s_navigationPrev = output<Carousel.EventsParams['navigationPrev']>({ alias: 'navigationPrev' });
    s_navigationShow = output<Carousel.EventsParams['navigationShow']>({ alias: 'navigationShow' });
    s_observerUpdate = output<Carousel.EventsParams['observerUpdate']>({ alias: 'observerUpdate' });
    s_orientationchange = output<Carousel.EventsParams['orientationchange']>({ alias: 'orientationchange' });
    s_paginationHide = output<Carousel.EventsParams['paginationHide']>({ alias: 'paginationHide' });
    s_paginationRender = output<Carousel.EventsParams['paginationRender']>({ alias: 'paginationRender' });
    s_paginationShow = output<Carousel.EventsParams['paginationShow']>({ alias: 'paginationShow' });
    s_paginationUpdate = output<Carousel.EventsParams['paginationUpdate']>({ alias: 'paginationUpdate' });
    s_progress = output<Carousel.EventsParams['progress']>({ alias: 'progress' });
    s_reachBeginning = output<Carousel.EventsParams['reachBeginning']>({ alias: 'reachBeginning' });
    s_reachEnd = output<Carousel.EventsParams['reachEnd']>({ alias: 'reachEnd' });
    s_realIndexChange = output<Carousel.EventsParams['realIndexChange']>({ alias: 'realIndexChange' });
    s_resize = output<Carousel.EventsParams['resize']>({ alias: 'resize' });
    s_scrollbarDragEnd = output<Carousel.EventsParams['scrollbarDragEnd']>({ alias: 'scrollbarDragEnd' });
    s_scrollbarDragMove = output<Carousel.EventsParams['scrollbarDragMove']>({ alias: 'scrollbarDragMove' });
    s_scrollbarDragStart = output<Carousel.EventsParams['scrollbarDragStart']>({ alias: 'scrollbarDragStart' });
    s_setTransition = output<Carousel.EventsParams['setTransition']>({ alias: 'setTransition' });
    s_setTranslate = output<Carousel.EventsParams['setTranslate']>({ alias: 'setTranslate' });
    s_slideChange = output<Carousel.EventsParams['slideChange']>({ alias: 'slideChange' });
    s_slideChangeTransitionEnd = output<Carousel.EventsParams['slideChangeTransitionEnd']>({ alias: 'slideChangeTransitionEnd' });
    s_slideChangeTransitionStart = output<Carousel.EventsParams['slideChangeTransitionStart']>({ alias: 'slideChangeTransitionStart' });
    s_slideNextTransitionEnd = output<Carousel.EventsParams['slideNextTransitionEnd']>({ alias: 'slideNextTransitionEnd' });
    s_slideNextTransitionStart = output<Carousel.EventsParams['slideNextTransitionStart']>({ alias: 'slideNextTransitionStart' });
    s_slidePrevTransitionEnd = output<Carousel.EventsParams['slidePrevTransitionEnd']>({ alias: 'slidePrevTransitionEnd' });
    s_slidePrevTransitionStart = output<Carousel.EventsParams['slidePrevTransitionStart']>({ alias: 'slidePrevTransitionStart' });
    s_slideResetTransitionEnd = output<Carousel.EventsParams['slideResetTransitionEnd']>({ alias: 'slideResetTransitionEnd' });
    s_slideResetTransitionStart = output<Carousel.EventsParams['slideResetTransitionStart']>({ alias: 'slideResetTransitionStart' });
    s_sliderFirstMove = output<Carousel.EventsParams['sliderFirstMove']>({ alias: 'sliderFirstMove' });
    s_sliderMove = output<Carousel.EventsParams['sliderMove']>({ alias: 'sliderMove' });
    s_slidesGridLengthChange = output<Carousel.EventsParams['slidesGridLengthChange']>({ alias: 'slidesGridLengthChange' });
    s_slidesLengthChange = output<Carousel.EventsParams['slidesLengthChange']>({ alias: 'slidesLengthChange' });
    s_slidesUpdated = output<Carousel.EventsParams['slidesUpdated']>({ alias: 'slidesUpdated' });
    s_snapGridLengthChange = output<Carousel.EventsParams['snapGridLengthChange']>({ alias: 'snapGridLengthChange' });
    s_snapIndexChange = output<Carousel.EventsParams['snapIndexChange']>({ alias: 'snapIndexChange' });
    s_tap = output<Carousel.EventsParams['tap']>({ alias: 'tap' });
    s_toEdge = output<Carousel.EventsParams['toEdge']>({ alias: 'toEdge' });
    s_touchEnd = output<Carousel.EventsParams['touchEnd']>({ alias: 'touchEnd' });
    s_touchMove = output<Carousel.EventsParams['touchMove']>({ alias: 'touchMove' });
    s_touchMoveOpposite = output<Carousel.EventsParams['touchMoveOpposite']>({ alias: 'touchMoveOpposite' });
    s_touchStart = output<Carousel.EventsParams['touchStart']>({ alias: 'touchStart' });
    s_transitionEnd = output<Carousel.EventsParams['transitionEnd']>({ alias: 'transitionEnd' });
    s_transitionStart = output<Carousel.EventsParams['transitionStart']>({ alias: 'transitionStart' });
    s_unlock = output<Carousel.EventsParams['unlock']>({ alias: 'unlock' });
    s_update = output<Carousel.EventsParams['update']>({ alias: 'update' });
    s_zoomChange = output<Carousel.EventsParams['zoomChange']>({ alias: 'zoomChange' });

    swiperRef = signal<Swiper | null>(null);
    swiperRef$ = toObservable(this.swiperRef);

    params = computed(() => {
        const params: Carousel.Options = {};
        Object.assign(params, Swiper.defaults);
        Object.assign(params, Swiper.extendedDefaults);

        params._emitClasses = true;

        const modules: SwiperModule[] = [];

        Carousel.PARAMS_KEYS.forEach(key => {
            const _key = key.replace(/^_/, '');

            if (this[_key]() !== undefined) {
                params[_key] = this[_key]();
            }
        });

        // Modules
        modules.push(A11y);
        if (this.a11y()) {
            params.a11y = this.a11y();
        }

        if (this.autoplay()) {
            modules.push(Autoplay);
            params.autoplay = this.autoplay();
        }

        if (this.cardsEffect()) {
            modules.push(EffectCards);
            params.cardsEffect = this.cardsEffect();
        }

        if (this.controller()) {
            modules.push(Controller);
            params.controller = this.controller();
        }

        if (this.coverflowEffect()) {
            modules.push(EffectCoverflow);
            params.coverflowEffect = this.coverflowEffect();
        }

        if (this.creativeEffect()) {
            modules.push(EffectCreative);
            params.creativeEffect = this.creativeEffect();
        }

        if (this.cubeEffect()) {
            modules.push(EffectCube);
            params.cubeEffect = this.cubeEffect();
        }

        if (this.fadeEffect()) {
            modules.push(EffectFade);
            params.fadeEffect = this.fadeEffect();
        }

        if (this.flipEffect()) {
            modules.push(EffectFlip);
            params.flipEffect = this.flipEffect();
        }

        if (this.freeMode()) {
            modules.push(FreeMode);
            params.freeMode = this.freeMode();
        }

        if (this.grid()) {
            modules.push(Grid);
            params.grid = this.grid();
        }

        if (this.hashNavigation()) {
            modules.push(HashNavigation);
            params.hashNavigation = this.hashNavigation();
        }

        if (this.history()) {
            modules.push(History);
            params.history = this.history();
        }

        if (this.keyboard()) {
            modules.push(Keyboard);
            params.keyboard = this.keyboard();
        }

        if (this.marquee()) {
            modules.push(Marquee);
            params.marquee = this.marquee();
        }

        if (this.mousewheel()) {
            modules.push(Mousewheel);
            params.mousewheel = this.mousewheel();
        }

        if (this.navigation()) {
            modules.push(Navigation);
            params.navigation = this.navigation();
        }

        if (this.pagination()) {
            modules.push(Pagination);
            params.pagination = this.pagination();
        }

        if (this.parallax()) {
            modules.push(Parallax);
            params.parallax = this.parallax();
        }

        if (this.scrollbar()) {
            modules.push(Scrollbar);
            params.scrollbar = this.scrollbar();
        }

        if (this.thumbs()) {
            modules.push(Thumbs);
            params.thumbs = this.thumbs();
        }

        if (this.zoom()) {
            modules.push(Zoom);
            params.zoom = this.zoom();
        }

        // Attach events
        const on = {};

        Object.keys(this)
            .filter(key => key.startsWith('s_'))
            .forEach(key => {
                const event = key.replace('s_', '') as keyof Carousel.EventsParams;

                on[event] = (...args: any[]) => this.event(event, key, args);
            });

        params.on = on;

        return <Carousel.Options>{
            modules,
            ...params
        };
    });
    slides = signal<CarouselSlideDirective[]>([]);
    initialized = signal(false);

    protected swiperContainer = viewChild<ElementRef<SwiperContainer>>('swiperContainerRef');
    protected prevElRef = viewChild<ElementRef<HTMLElement>>('prevElRef');
    protected nextElRef = viewChild<ElementRef<HTMLElement>>('nextElRef');
    protected paginationElRef = viewChild<ElementRef<HTMLElement>>('paginationElRef');
    protected scrollbarElRef = viewChild<ElementRef<HTMLElement>>('scrollbarElRef');
    protected placeholderTpl = contentChild<TemplateRef<unknown>>('placeholderTpl');
    protected placeholderSlideTpl = contentChild<TemplateRef<unknown>>('placeholderSlideTpl');

    protected carouselSlides = contentChildren(CarouselSlideDirective, { descendants: false });

    protected document = inject(DOCUMENT);
    protected changeDetectorRef = inject(ChangeDetectorRef);
    protected logger = inject(Logger);
    protected platformId = inject(PLATFORM_ID);
    protected timeoutService = injectTimeoutService();

    private destroyed = signal(false);

    constructor() {
        effect(() => {
            const carouselSlides = this.carouselSlides();

            untracked(() => {
                this.slidesChanges(carouselSlides);
            });
        });

        typeof window !== 'undefined' &&
            (<any>window).SwiperElementRegisterParams &&
            (<any>window).SwiperElementRegisterParams(['marquee']);

        // Wait for the next render to initialize the swiper
        afterNextRender(() => {
            this.initialized.set(true);

            this.detectChanges();

            this.initSwiper();
        });
    }

    get isPlatformBrowser() {
        return isPlatformBrowser(this.platformId);
    }

    readonly isDestroyed = computed(() => {
        return !this.swiperRef() || this.swiperRef() === null || !!this.swiperRef()?.destroyed;
    });

    ngOnInit(): void {
        register();
    }

    ngOnChanges(changedParams: SimpleChanges) {
        this.updateSwiper();
    }

    ngOnDestroy() {
        this.detachEvents();

        this.destroy();

        this.destroyed.set(true);
    }

    initSwiper() {
        if (!this.swiperContainer()) {
            return;
        }

        const swiperEl = this.swiperContainer()!.nativeElement;
        const swiperParams = this.params();

        Object.assign(swiperEl, swiperParams);

        swiperEl.initialize();

        this.destroyed.set(false);

        this.swiperRef.set(swiperEl.swiper);
    }

    updateSwiper() {
        if (!this.swiperContainer()) {
            return;
        }

        const swiperEl = this.swiperContainer()!.nativeElement;
        const swiperParams = this.params();
        const changedParams = {};

        Carousel.PARAMS_KEYS.forEach(key => {
            if (key.startsWith('_')) {
                const _key = key.replace(/^_/, '');

                if (this.swiperRef()?.params) {
                    changedParams[_key] = swiperParams[_key];
                }
            }
        });

        Object.assign(swiperEl, changedParams);
    }

    protected destroy() {
        if (!this.isDestroyed()) {
            try {
                this.swiperRef()?.destroy(true, false);
                this.swiperRef.set(null);
            } catch (e) {
                this.logger.error(e);
            }

            const swiperEl = this.swiperContainer()?.nativeElement;

            if (swiperEl) {
                (<any>swiperEl).initialized = false;
            }
        }
    }

    protected slidesChanges(value: readonly CarouselSlideDirective[] = []) {
        const reInit = this.slides().length !== value.length;

        if (this.isPlatformBrowser && reInit && !this.isDestroyed()) {
            this.destroy();
        }

        const slides = value.map((slide: CarouselSlideDirective, index: number) => {
            slide.slideIndex = index;
            slide.classNames = this.slideClass() || '';

            return slide;
        });

        this.slides.set(slides);

        this.detectChanges();

        if (reInit || this.isDestroyed()) {
            this.initSwiper();
        } else {
            this.updateSwiper();
        }
    }

    protected detachEvents(): void {
        if (!this.swiperRef()) return;

        Object.keys(this)
            .filter(key => key.startsWith('s_'))
            .forEach(key => {
                const event = key.replace('s_', '') as keyof Carousel.EventsParams;

                this.swiperRef()?.off(event, (...args: any[]) => this.event(event, key, args));
            });
    }

    protected event(event: string, key: string, args: any[]) {
        if (this.destroyed()) {
            return;
        }

        if (!this.isDestroyed() || (!this.swiperRef() && Carousel.EVENTS_BEFORE_INIT.includes(event))) {
            this[key].emit(args);

            if (event === 'slideChange') {
                const [swiper] = args;

                if (!swiper || swiper.destroyed) return;

                swiper.slides.forEach((slide: HTMLElement, index: number) => {
                    const dataIndex = slide.getAttribute('data-swiper-slide-index');
                    const slideIndex = dataIndex ? parseInt(dataIndex) : index;

                    if (this.slides()[slideIndex]) {
                        this.slides()[slideIndex].classNames = slide.className;
                    }
                });
            }
        }
    }

    protected detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
